/*
 * <umm-ritual-quiz>  Discover Your Ritual (Oct 2026)
 * Four answers -> one of four rituals, composed from live products and stock.
 * Sold-out products are replaced by their nearest equivalent or left out.
 */
if (!customElements.get('umm-ritual-quiz')) {
  customElements.define('umm-ritual-quiz', class UmmRitualQuiz extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      try {
        this.products = JSON.parse(this.querySelector('[data-rq-products]').textContent);
      } catch (e) {
        this.products = {};
      }
      this.order = ['intent', 'skin', 'depth', 'scent'];
      this.answers = {};
      this.index = 0;
      this.steps = {};
      this.querySelectorAll('[data-rq-step]').forEach((el) => { this.steps[el.dataset.rqStep] = el; });
      this.result = this.querySelector('[data-rq-result]');
      this.backBtn = this.querySelector('[data-rq-back]');
      this.countEl = this.querySelector('[data-rq-count]');
      this.barEl = this.querySelector('[data-rq-bar]');
      this.progress = this.querySelector('[data-rq-progress]');
      this.nav = this.querySelector('[data-rq-nav]');

      this.querySelectorAll('.umm-rq__opt').forEach((opt) => {
        opt.addEventListener('click', () => this.choose(opt));
      });
      this.backBtn.addEventListener('click', () => this.go(this.index - 1));
      this.querySelector('[data-rq-restart]').addEventListener('click', () => this.restart());
    }

    choose(opt) {
      const step = opt.closest('[data-rq-step]');
      step.querySelectorAll('.umm-rq__opt').forEach((o) => o.setAttribute('aria-pressed', o === opt ? 'true' : 'false'));
      this.answers[step.dataset.rqStep] = opt.dataset.value;
      if (this.index === 0 && window.UMMRitualCart) window.UMMRitualCart.track('umm_quiz_start', {});
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        if (this.index < this.order.length - 1) this.go(this.index + 1);
        else this.showResult();
      }, 260);
    }

    go(i) {
      if (i < 0) return;
      this.index = i;
      this.order.forEach((key, n) => { this.steps[key].hidden = n !== i; });
      this.result.hidden = true;
      this.progress.hidden = false;
      this.nav.hidden = false;
      this.backBtn.hidden = i === 0;
      this.countEl.textContent = '0' + (i + 1) + ' / 04';
      this.barEl.style.width = ((i + 1) * 25) + '%';
      this.focusTop(this.steps[this.order[i]].querySelector('.umm-rq__opt[aria-pressed="true"]') || this.steps[this.order[i]].querySelector('.umm-rq__opt'));
    }

    restart() {
      this.answers = {};
      this.querySelectorAll('.umm-rq__opt').forEach((o) => o.setAttribute('aria-pressed', 'false'));
      this.go(0);
    }

    focusTop(el) {
      const rect = this.getBoundingClientRect();
      if (rect.top < 0 || rect.top > window.innerHeight * 0.6) {
        this.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      }
      if (el) el.focus({ preventScroll: true });
    }

    /* ---- Product helpers ---- */

    variant(key, titleMatch) {
      const p = this.products[key];
      if (!p || !p.variants) return null;
      let list = p.variants.filter((v) => v.available);
      if (titleMatch) {
        const match = list.find((v) => v.title.toLowerCase().indexOf(titleMatch) !== -1);
        list = match ? [match] : list;
      }
      return list[0] || null;
    }

    pick(keys) {
      for (const key of keys) {
        if (this.variant(key)) return key;
      }
      return null;
    }

    /* ---- Ritual composition ---- */

    compose(a) {
      const depth = parseInt(a.depth, 10) || 1;
      const items = [];
      const used = {};
      const add = (key, step, titleMatch) => {
        if (!key || used[key]) return;
        const v = this.variant(key, titleMatch);
        if (!v) return;
        used[key] = true;
        items.push({ key: key, step: step, variant: v, product: this.products[key] });
      };

      // Preparation
      add('oil', 'prep');
      if (depth === 3 || (a.intent === 'calm' && depth >= 2)) add('diya', 'prep');

      // Cleansing
      if (a.scent === 'gel') add('gel', 'cleanse');
      else add('bar', 'cleanse', a.scent === 'sandalwood' ? 'sandalwood' : 'turmeric');
      if (!items.some((i) => i.step === 'cleanse')) add(this.pick(['gel', 'bar']), 'cleanse');

      // Exfoliation
      let polish = a.intent === 'radiance' || a.skin === 'dull' ||
        (a.intent === 'nourish' && depth >= 2) || depth === 3;
      if (a.skin === 'delicate' && a.intent !== 'radiance') polish = false;
      if (polish) add(this.pick(a.intent === 'radiance' || a.skin === 'dull' ? ['dust', 'scrub'] : ['scrub', 'dust']), 'polish');

      // Moisturizing
      const richest = this.pick(['creme', 'elixir']);
      if (a.intent === 'nourish') {
        add(richest, 'nourish');
        if (depth >= 2 || a.skin === 'dry') add('elixir', 'nourish');
        if (depth === 3) add('veil', 'nourish');
      } else if (a.intent === 'radiance') {
        add('elixir', 'nourish');
        if (depth === 3 || a.skin === 'dry') add(depth === 3 ? 'veil' : richest, 'nourish');
      } else if (a.intent === 'calm') {
        add('veil', 'nourish');
        if (depth >= 2 || a.skin === 'dry') add(richest, 'nourish');
      } else {
        add('kansa', 'nourish');
        if (depth >= 2 || a.skin === 'dry') add('elixir', 'nourish');
        if (depth === 3) add('marma', 'nourish');
      }
      if (!items.some((i) => i.step === 'nourish')) add(this.pick(['elixir', 'creme', 'veil']), 'nourish');

      // Practice
      if (depth === 3) add('guide', 'practice');
      return items;
    }

    showResult() {
      const a = this.answers;
      const ritual = UmmRitualQuiz.RITUALS[a.intent] || UmmRitualQuiz.RITUALS.nourish;
      const items = this.compose(a);
      const r = this.result;
      r.querySelector('[data-rq-skt]').textContent = ritual.skt;
      r.querySelector('[data-rq-name]').textContent = ritual.name;
      r.querySelector('[data-rq-meaning]').textContent = ritual.meaning;
      r.querySelector('[data-rq-desc]').textContent = ritual.desc;

      const list = r.querySelector('[data-rq-list]');
      list.innerHTML = '';
      let total = 0;
      items.forEach((item) => {
        total += item.variant.price;
        const li = document.createElement('li');
        li.className = 'umm-rq__item';
        const title = item.product.title + (item.key === 'bar' ? ', ' + item.variant.title : '');
        li.innerHTML =
          '<img alt="" loading="lazy" width="64" height="64">' +
          '<div><p class="umm-rq__item-step"></p><p class="umm-rq__item-title"><a></a></p><p class="umm-rq__item-why"></p></div>' +
          '<span class="umm-rq__item-price"></span>';
        const img = li.querySelector('img');
        if (item.product.image) img.src = item.product.image; else img.remove();
        li.querySelector('.umm-rq__item-step').textContent = UmmRitualQuiz.STEPS[item.step];
        const link = li.querySelector('a');
        link.textContent = title;
        link.href = item.product.url + (item.key === 'bar' ? '?variant=' + item.variant.id : '');
        li.querySelector('.umm-rq__item-why').textContent = UmmRitualQuiz.WHY[item.key === 'bar' ? 'bar-' + (item.variant.title.toLowerCase().indexOf('sandal') !== -1 ? 'sandalwood' : 'turmeric') : item.key] || '';
        li.querySelector('.umm-rq__item-price').textContent = item.variant.price === 0 ? 'Complimentary' : this.money(item.variant.price);
        list.appendChild(li);
      });
      r.querySelector('[data-rq-total]').textContent = this.money(total);

      const ids = items.map((i) => i.variant.id);
      const addBtn = r.querySelector('[data-rq-add]');
      addBtn.setAttribute('data-umm-add', ids.join(','));
      addBtn.disabled = ids.length === 0;
      const compose = r.querySelector('[data-rq-compose]');
      const base = this.dataset.composeUrl || '/pages/build-your-ritual';
      compose.href = base + (base.indexOf('?') === -1 ? '?' : '&') + 'ritual=' + ids.join(',') + '&from=consultation';

      this.order.forEach((key) => { this.steps[key].hidden = true; });
      this.progress.hidden = true;
      this.nav.hidden = true;
      r.hidden = false;
      this.focusTop(null);
      r.focus({ preventScroll: true });
      if (window.UMMRitualCart) {
        window.UMMRitualCart.track('umm_quiz_complete', { ritual: a.intent, skin: a.skin, depth: a.depth, scent: a.scent, items: ids.length, value: total / 100 });
      }
    }

    money(cents) {
      if (window.UMMRitualCart) return window.UMMRitualCart.money(cents);
      return '$' + (cents / 100).toFixed(2);
    }
  });

  const Q = customElements.get('umm-ritual-quiz');
  Q.STEPS = {
    prep: 'Preparation',
    cleanse: 'Cleansing',
    polish: 'Exfoliation',
    nourish: 'Moisturizing',
    practice: 'The Practice'
  };
  Q.RITUALS = {
    nourish: {
      skt: 'स्नेह',
      name: 'The Sneha Ritual',
      meaning: 'Sneha: in Sanskrit, both oil and loving care.',
      desc: 'A ritual of deep nourishment. Warm oil, a gentle cleanse and a golden seal of moisture leave the skin supple, soft and comforted.'
    },
    radiance: {
      skt: 'तेजस्',
      name: 'The Tejas Ritual',
      meaning: 'Tejas: radiance, the inner light of the body.',
      desc: 'A ritual of renewal and light. Warm oil, a botanical polish and a luminous finish reveal smooth, glowing skin.'
    },
    calm: {
      skt: 'शान्ति',
      name: 'The Shanti Ritual',
      meaning: 'Shanti: peace, the stillness that follows a true bath.',
      desc: 'A ritual of stillness. Warm oil, soft aromatics and a weightless veil of hydration turn the bath into a moment of rest.'
    },
    restore: {
      skt: 'मर्म',
      name: 'The Marma Ritual',
      meaning: 'Marma: the vital points honored in classical Indian bodywork.',
      desc: 'A ritual of release. Warm oil and mindful massage awaken circulation and leave the body light, restored and at ease.'
    }
  };
  Q.WHY = {
    oil: 'Before the bath, on dry skin. Abhyanga (अभ्यङ्ग), the warm oil massage that begins every UMM ritual.',
    diya: 'Jasmine candlelight to set the mood.',
    gel: 'A silken cleanse with saffron, amla and ashwagandha.',
    'bar-turmeric': 'Goat milk cleansing bar with turmeric and ashwagandha. Brightening and nourishing.',
    'bar-sandalwood': 'Goat milk cleansing bar with sandalwood and manjistha. Gently resurfacing.',
    scrub: 'After cleansing. A creamy polish with gold, saffron and bakuchi for a satin finish.',
    dust: 'Udvartana (उद्वर्तन), a botanical polish and masque with turmeric, saffron and sandalwood.',
    creme: 'Daily nourishment for face and body with silk peptide, bakuchi and saffron.',
    elixir: 'On damp skin after the bath. Seals in moisture and whole-body radiance.',
    veil: 'A weightless mist of hydration and jasmine sambac.',
    kansa: 'Bronze Kansa massage over oiled skin for a sculpted, rested feel.',
    marma: 'Gentle pressure on marma points to release and ground.',
    guide: 'Your 21-day guide to the practice.'
  };
}
